import { extendTheme } from '@chakra-ui/react';

/**
 * O-ID Design System
 *
 * Color scheme : #ff3c23 · #16213a · #efefef · #000000 · #ffffff
 * Type scale   : golden ratio (φ = 1.618) base 17 px
 *                11 → 17 → 27.5 → 44.5 → 72 → 116 px
 * Line height  : 1.618 (φ) for body
 * Column split : 38.2 % / 61.8 % (golden ratio)
 * Spacing      : Fibonacci 8-13-21-34-55-89-144 px
 *
 * WCAG AA contrast (verified):
 *   #16213a on #efefef  14.6:1 ✓
 *   #4a5568 on #efefef   6.1:1 ✓
 *   #ffffff on #16213a  16.7:1 ✓
 *   #ffffff on #000000  21.0:1 ✓
 *   #ff3c23 on #16213a   4.7:1 ✓ (AA normal text)
 *   #ff3c23 on #000000   5.3:1 ✓ (AA)
 *   #ff3c23 on light bg — used only at 600 wt + large size (AA large text ✓)
 */

const theme = extendTheme({
  config: { initialColorMode: 'light', useSystemColorMode: false },

  colors: {
    oid: {
      bg:          '#efefef',
      bgWhite:     '#ffffff',
      bgTint:      'rgba(22,33,58,0.06)',
      bgDark:      '#16213a',
      bgBlack:     '#000000',
      text:        '#16213a',
      textLight:   '#ffffff',
      muted:       '#4a5568',
      mutedLight:  'rgba(239,239,239,0.72)',
      accent:      '#ff3c23',
      accentHover: '#e02e18',
      border:      'rgba(22,33,58,0.13)',
      borderLight: 'rgba(255,255,255,0.12)',
    },
  },

  fonts: {
    heading: `'Zen Kaku Gothic Antique', 'Helvetica Neue', Arial, sans-serif`,
    body:    `'Zen Kaku Gothic Antique', 'Helvetica Neue', Arial, sans-serif`,
  },

  styles: {
    global: {
      html: { scrollBehavior: 'smooth', fontSize: '16px' },
      '::-webkit-scrollbar': { width: '6px' },
      '::-webkit-scrollbar-track': { background: '#efefef' },
      '::-webkit-scrollbar-thumb': {
        background: '#ff3c23',
        borderRadius: '3px',
      },
      '::-webkit-scrollbar-thumb:hover': { background: '#e02e18' },
      '*': { scrollbarWidth: 'thin', scrollbarColor: '#ff3c23 #efefef' },
      body: {
        bg: 'oid.bg',
        color: 'oid.text',
        fontWeight: 400,
        lineHeight: 1.618,
        overflowX: 'hidden',
      },
      ':root': { '--nav-h': '64px' },
      '*:focus-visible': { outline: '2px solid #ff3c23', outlineOffset: '3px' },
      '*:focus:not(:focus-visible)': { outline: 'none', boxShadow: 'none' },
      '@keyframes slowSpin': {
        from: { transform: 'rotate(0deg)' },
        to:   { transform: 'rotate(360deg)' },
      },
      '@keyframes lineSwipe': {
        '0%': { left: '-100%' }, '50%': { left: '0%' }, '100%': { left: '100%' },
      },
      '@keyframes scrollPulse': {
        '0%, 100%': { opacity: 0.45 }, '50%': { opacity: 1 },
      },
    },
  },

  components: {
    Tabs: {
      variants: {
        oid: {
          tab: {
            fontFamily: `'Zen Kaku Gothic Antique', sans-serif`,
            fontWeight: 600,
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#4a5568',
            boxShadow: 'inset 0 -2px 0 transparent',
            py: '14px',
            px: { base: 4, md: 6 },
            minW: '44px',
            lineHeight: 1.3,
            textAlign: 'left',
            transition: 'color 0.18s ease, box-shadow 0.18s ease',
            _selected:      { color: '#16213a', boxShadow: 'inset 0 -2px 0 #ff3c23',
                              _focus: { boxShadow: 'inset 0 -2px 0 #ff3c23' } },
            _hover:         { color: '#16213a', textDecoration: 'none' },
            _focus:         { boxShadow: 'none' },
            _focusVisible:  { outline: '2px solid #ff3c23', outlineOffset: '2px', borderRadius: '2px' },
          },
          tablist: {
            borderBottom: '2px solid rgba(22,33,58,0.13)',
            overflowX: 'auto',
            gap: 0,
            /* hide scrollbar on all browsers */
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          },
          tabpanel: { p: 0, pt: 10 },
          tabindicator: { display: 'none' },
        },
      },
    },
    Link: {
      baseStyle: {
        _focusVisible: { outline: '2px solid #ff3c23', outlineOffset: '3px', borderRadius: '2px' },
      },
    },
    Button: {
      baseStyle: {
        minW: '44px', minH: '44px',
        fontFamily: `'Zen Kaku Gothic Antique', sans-serif`,
        fontWeight: 600,
        letterSpacing: '0.06em',
        borderRadius: '2px',
        _focusVisible: { outline: '2px solid #ff3c23', outlineOffset: '3px' },
      },
      defaultProps: { variant: 'solid' },
      variants: {
        solid: {
          bg: '#ff3c23',
          color: 'white',
          _hover: { bg: '#16213a', _disabled: { bg: '#ff3c23' } },
          _active: { bg: '#16213a' },
        },
        outline: {
          borderColor: '#ff3c23',
          color: '#ff3c23',
          _hover: { bg: '#16213a', borderColor: '#16213a', color: 'white' },
          _active: { bg: '#16213a' },
        },
        ghost: {
          color: '#ff3c23',
          _hover: { bg: 'rgba(255,60,35,0.08)' },
        },
      },
    },
  },
});

export default theme;
